import React, { ReactNode } from 'react';
import { Route } from 'react-router-dom';

import { lazy } from '@components/common/routeLoading';
import { Path } from '@components/routes/constants';
import { entitled } from '@components/routes/routeEntitlement';
import type { UseIsRouteEnabled } from '@components/routes/routeEntitlement';
import { useCanCreateSource } from '@components/routes/entitlements';
import { ACTION, SECTION } from '@hooks/useDocumentTitle/constants';
import useStores from '@stores/useStores';
import { ActionType, ResourceType, useActionGuard } from '@hooks/useActionGuard';

/**
 * Kept local rather than in @components/routes/entitlements: these gate the rETL
 * creation flows only. `/addSourceV1` reuses the shared create-source check —
 * it is the same rule as `/addSource`, reading the definition id from
 * location.state.
 */
const useCanCreateSqlModel: UseIsRouteEnabled = () => {
  const { featuresStore } = useStores();
  const { canPerformAction } = useActionGuard();

  return (
    featuresStore.has('MODELS') &&
    canPerformAction({
      old: { resourceType: 'model', action: 'create' },
      new: { actionType: ActionType.CREATE, resource: { type: ResourceType.SQL_MODEL } },
    }).isAllowed
  );
};

const useCanCreateAudience: UseIsRouteEnabled = () => {
  const { featuresStore } = useStores();
  const { canPerformAction } = useActionGuard();

  return (
    featuresStore.has('AUDIENCES') &&
    canPerformAction({
      old: { resourceType: 'workspace', action: 'edit' },
      new: { actionType: ActionType.CREATE, resource: { type: ResourceType.AUDIENCE } },
    }).isAllowed
  );
};

/**
 * Every element is lazy — see the note in @components/cohorts/routes on why the
 * route definitions must not reference page modules directly.
 */
const RetlWorkflowProvider = lazy(() =>
  import(/* webpackChunkName: "RETLWorkflowRoutes" */ './routesLayout').then((m) => ({
    default: m.RetlWorkflowProvider,
  })),
);

const CreateRETLSource = lazy(() =>
  import(/* webpackChunkName: "RETLWorkflowRoutes" */ './createRetlSource').then((m) => ({
    default: m.CreateRETLSource,
  })),
);

const CreateModelV2 = lazy(() =>
  import(/* webpackChunkName: "RETLWorkflowRoutes" */ './createModel').then((m) => ({
    default: m.CreateModelV2,
  })),
);

const CreateAudienceV2 = lazy(() =>
  import(/* webpackChunkName: "RETLWorkflowRoutes" */ './createAudience').then((m) => ({
    default: m.CreateAudienceV2,
  })),
);

const withRetlStore = (page: ReactNode) => <RetlWorkflowProvider>{page}</RetlWorkflowProvider>;

/**
 * See the note in @components/botManagement/routes on why these are definitions.
 *
 * Each flow keeps its trailing splat: the pages own further in-flow navigation
 * below these prefixes.
 *
 * This replaces a hand-rolled dispatcher that matched `location.pathname` against
 * a prefix list, which existed because a descendant <Routes> only sees the path
 * after its parent prefix and so could never match these absolute paths. Being
 * real routes, they no longer need that workaround.
 */
export const retlWorkflowRoutes = [
  <Route
    key={Path.ADD_SOURCE_V1}
    path={`${Path.ADD_SOURCE_V1}/*`}
    element={entitled(useCanCreateSource, withRetlStore(<CreateRETLSource />))}
    handle={{ section: SECTION.sources, action: ACTION.create }}
  />,
  <Route
    key={Path.MODELS_SETUP}
    path={`${Path.MODELS_SETUP}/*`}
    element={entitled(useCanCreateSqlModel, withRetlStore(<CreateModelV2 />))}
    handle={{ section: SECTION.models, action: ACTION.create }}
  />,
  <Route
    key={Path.AUDIENCE_SETUP}
    path={`${Path.AUDIENCE_SETUP}/*`}
    element={entitled(useCanCreateAudience, withRetlStore(<CreateAudienceV2 />))}
    handle={{ section: SECTION.audiences, action: ACTION.create }}
  />,
];
